/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import {
  Component, OnInit, Output, OnChanges, Input, EventEmitter, SimpleChanges,
  OnDestroy
} from '@angular/core';
import { trigger, state, animate, transition, style } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from '../../shared/services/common.service';
import { BookmarkPopoverService } from './bookmark-popover.service';
import { BookmarkSliderService } from '../services/slider.service';
import { eventSubscriber } from '../../shared/common-child.interface';

@Component({
  selector: 'bookmark-popover-content',
  templateUrl: './bookmark-popover.component.html',
  styleUrls: ['./bookmark-popover.component.scss'],
  providers: [CommonService, BookmarkPopoverService],
  animations: [
    trigger('panelVisibility', [
      state('open', style({
        width: '24rem'
      })),
      state('collapse', style({
        width: '4rem'
      })),
      transition('open => collapse', [animate('2s ease', style({ width: '4rem' }))]),
      transition('collapse => open', [animate('1s ease', style({ width: '24rem' }))])
    ]),
    trigger('contentVisibility', [
      state('open', style({
        margin: '0rem 0rem 0rem 24rem'
      })),
      state('collapse', style({
        margin: '0rem 0rem 0rem 4rem'
      })),
      transition('open => collapse', [animate('2s ease', style({ margin: '0rem 0rem 0rem 4rem' }))]),
      transition('collapse => open', [animate('1s ease', style({ margin: '0rem 0rem 0rem 24rem' }))])
    ]),
    trigger('panelSlider', [
      state('show', style({
        'right': '0'
      })),
      state('hide', style({
        'right': '-56rem'
      })),
      transition('show => hide', [animate('1s ease', style({ 'right': '-56rem' }))]),
      transition('hide => show', [animate('1s ease', style({ 'right': '0' }))])
    ]),
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateX(0)',
        'visibility': 'visible'
      })),
      state('out', style({
        transform: 'translateX(100%)',
        'visibility': 'hidden'
      })),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out'))
    ])
  ]
})

export class BookmarkPopoverComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedMemberData: any;
  @Input() fromPage = '';
  @Input() source = '';
  @Input() selectedGroupLabel = '';
  @Input() groupId = '';
  @Input() popRef: any;
  @Input() homeMetaData!: { modules: Array<any> };
  @Output() onSaveSuccess: EventEmitter<boolean> = new EventEmitter();
  private undoBookmarkName = '';
  private subscription: any;

  constructor(
    private commonService: CommonService,
    private bookmarkPopoverService: BookmarkPopoverService,
    private translate: TranslateService,
    private bookmarkSliderService: BookmarkSliderService
  ) {
    this.closeBookmarkSlider = this.closeBookmarkSlider.bind(this);
    eventSubscriber(commonService.subscription, this.closeBookmarkSlider);
  }

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedMemberData'] && changes['selectedMemberData'].currentValue) {
      this.initialize();
    }
  }

  public getTranslationWithKey(key: string): string {
    return this.commonService.getTranslatedVal(key);
  }

  private closeBookmarkSlider() {
    this.bookmarkSliderService.hide();
  }

  public closePopover() {
    this.popOverClose();
  }

  public onOkClick() {
    this.popOverClose();
  }

  private popOverClose() {
    this.initialize();
    // this.resetAll();
    if (this.popRef) {
      this.popRef.hide();
    }
  }

  private initialize() {
    this.selectedMemberData.isSaveSuccess = false;
    this.selectedMemberData.isRemovedSuccess = false;
    this.selectedMemberData.inEditMode = true;

    if (this.fromPage === 'Genealogies') {
      if (this.selectedMemberData.bookmarkDetails) {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.bookmarkDetails.name || '';
      } else {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.name + ' (' + this.selectedMemberData?.clientId + ') - '
          + this.selectedGroupLabel;
      }
    } else if (this.fromPage === 'Distributors') {
      if (this.selectedMemberData.bookmarkDetails) {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.bookmarkDetails.name || '';
      } else {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.name + ' (' + this.selectedMemberData?.clientId + ')';
      }
    } else if (this.fromPage === 'Reports') {
      if (this.selectedMemberData.bookmarkDetails) {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.bookmarkDetails.name || '';
      } else {
        this.selectedMemberData.bookmarkName = this.selectedMemberData.name ? this.selectedMemberData.name
          : 'Report [' + (new Date()).toLocaleDateString() + ']';
      }
    } else {
      this.selectedMemberData.bookmarkName = this.selectedMemberData.name + ' (' + this.selectedMemberData?.clientId + ')';
    }

    // capture name so as to use when use clicks on undo button
    this.undoBookmarkName = this.selectedMemberData.bookmarkName;

    if (this.selectedMemberData.bookmarkDetails) {
      this.selectedMemberData.inEditMode = false;
    }
  }

  ngOnDestroy() {
    eventSubscriber(this.commonService.subscription, this.closeBookmarkSlider, true);
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private commonErrorHandler(error: any) {
    this.selectedMemberData.isSaveSuccess = false;
    this.commonService.commonErrorHandler(error);
  }

  public saveBookmark(event: any) {
    event.stopPropagation();
    const payload: any = this.getRequestPayload();

    if (this.selectedMemberData.bookmarkDetails) {
      this.bookmarkPopoverService.updateBookmark(payload, this.selectedMemberData.bookmarkDetails.id)
        .then(this.saveBookmarkSH.bind(this), this.commonErrorHandler.bind(this));
    } else {
      this.bookmarkPopoverService.createBookmark(payload)
        .then(this.saveBookmarkSH.bind(this), this.commonErrorHandler.bind(this));
    }
  }

  private getRequestPayload() {
    const payload: any = {
      name: this.selectedMemberData.bookmarkName,
      moduleId: this.getModuleId(this.fromPage),
      source: this.source,
      gender: this.selectedMemberData.gender
    };

    if (this.fromPage !== 'Reports') {
      payload.clientID = this.selectedMemberData?.clientId;
      payload.participantId = this.selectedMemberData.id;

      if (this.selectedGroupLabel) {
        payload.groupName = this.selectedGroupLabel;
      }

      if (this.groupId) {
        payload.groupId = this.groupId;
      }
      // fix to save report id so as to show while navigating from Bookmark Slider page
      if (this.fromPage === 'Genealogies') {
        payload.reportId = this.selectedMemberData.selectedReport ? this.selectedMemberData.selectedReport.id : null;
      }
    }
    if ((this.fromPage === 'Reports') && this.selectedMemberData.id) {
      payload.reportId = this.selectedMemberData.id;
    }
    return payload;
  }

  private saveBookmarkSH(response: any) {
    const result = response; // Parse response body
    if (result && result.data) {
      const bookmarksData: Array<any> = result.data || [];
      const bookmarkModuleId = this.getModuleId(this.fromPage);
      const selectedBookmark = bookmarksData.find((bookmark) => {
        if (this.fromPage === 'Reports') {
          return (bookmark.reportId == this.selectedMemberData.id) && (bookmark.moduleId == bookmarkModuleId);
        } else if (this.fromPage === 'Genealogies') {
          return (bookmark.clientID == this.selectedMemberData?.clientId) && (bookmark.moduleId == bookmarkModuleId)
            && (bookmark.groupId == this.groupId);
        } else {
          return (bookmark.clientID === this.selectedMemberData?.clientId) && (bookmark.moduleId == bookmarkModuleId);
        }
      });

      // emit save sucess
      this.onSaveSuccess.emit(true);
      this.selectedMemberData.bookmarkDetails = selectedBookmark;
      this.selectedMemberData.bookmarkName = selectedBookmark.name;
      // // capture name so as to use when use clicks on undo button
      this.undoBookmarkName = this.selectedMemberData.bookmarkName;

      // this.initialize();
      this.selectedMemberData.isSaveSuccess = true;
      this.selectedMemberData.inEditMode = false;
      this.selectedMemberData.isRemovedSuccess = false;
    }
  }

  private getModuleId(fromPage: any) {
    const selectedModule = this.homeMetaData.modules.find(element => {
      return element.name === fromPage;
    });
    return (selectedModule ? selectedModule.id : '') || '';
  }

  public onManageBookmark(event: any) {
    // this.toggleBookmarksSlide();
    this.bookmarkSliderService.show();
    // this.closePopover();
    // event.stopPropagation();
    // let moveOrderLbl: any = this.translate.get("permissions.menus.BKM");
    // this.commonService.naviagtePanels(moveOrderLbl.value);
  }

  public toggleBookmarksSlide(event?: any) {
    if (!event) {
      this.bookmarkSliderService.toggle();
    } else {
      this.bookmarkSliderService.hide();
    }
  }

  editBookmark(event: any) {
    event.stopPropagation();
    this.selectedMemberData.inEditMode = true;
    this.selectedMemberData.isSaveSuccess = false;
    this.selectedMemberData.isRemovedSuccess = false;
  }

  deleteBookmark() {
    // id exists for existing Bookmark
    if (this.selectedMemberData.bookmarkDetails) {
      // api call
      this.bookmarkPopoverService.deleteBookmark(this.selectedMemberData.bookmarkDetails.id)
        .then(this.deleteBookmarkSH.bind(this), this.commonErrorHandler.bind(this));
    } else {
      this.selectedMemberData.isRemovedSuccess = true;
      this.selectedMemberData.isSaveSuccess = false;
      this.selectedMemberData.inEditMode = false;
    }
  }

  private deleteBookmarkSH() {
    // emit delete sucess
    this.onSaveSuccess.emit(false);

    this.selectedMemberData.bookmarkName = this.selectedMemberData.bookmarkDetails.name;
    // // capture name so as to use when use clicks on undo button
    this.undoBookmarkName = this.selectedMemberData.bookmarkName;
    this.selectedMemberData.isRemovedSuccess = true;
    this.selectedMemberData.isSaveSuccess = false;
    this.selectedMemberData.inEditMode = false;

    // remove reference, because undo should 
    // create a new Create request if it is saved after Undo
    delete this.selectedMemberData.bookmarkDetails;
  }

  undoBookmark() {
    // this.selectedMemberData.isRemovedSuccess = false;
    // this.selectedMemberData.isSaveSuccess = false;
    // this.selectedMemberData.inEditMode = true;
    // this.selectedMemberData.bookmarkName = this.undoBookmarkName;
    const payload: any = this.getRequestPayload();

    // create api call
    this.bookmarkPopoverService.createBookmark(payload)
      .then(this.saveBookmarkSH.bind(this), this.commonErrorHandler.bind(this));
  }

  private resetAll() {
    this.selectedMemberData.isRemovedSuccess = false;
    this.selectedMemberData.isSaveSuccess = false;
    this.selectedMemberData.inEditMode = true;
  }

}
